import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, Download, ShoppingCart, Car, Smartphone, Home, Gem, 
  User, Star, RefreshCw, Settings, Search, Filter, MapPin, 
  Plus, Eye, Share2, Clock, ChevronRight, ChevronDown, ArrowUpDown,
  Tag, Gavel, Lock, ShoppingBag, DollarSign, Building, Tv
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ICON_MAP = {
  Package, Download, ShoppingCart, Car, Smartphone, Home, Gem, 
  User, Star, RefreshCw, Settings
};

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh'
];

const P2PDeals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    listing_type: '',
    category_id: '',
    subcategory_id: '',
    search: '',
    city: '',
    state: '',
    sort_by: 'created_at'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchListings();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/p2p/categories');
      // Separate main categories and sub-categories
      const mainCats = response.data.filter(c => !c.parent_id);
      const subCats = response.data.filter(c => c.parent_id);
      setCategories(mainCats);
      setSubCategories(subCats);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Get sub-categories for selected category
  const getSubCategoriesForCategory = (categoryId) => {
    return subCategories.filter(sc => sc.parent_id === categoryId);
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.search) params.append('search', filters.search);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      params.append('sort_by', filters.sort_by);
      
      const response = await api.get(`/p2p/listings?${params.toString()}`);
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (iconName) => {
    return ICON_MAP[iconName] || Package;
  };

  const handleShare = async (listing) => {
    const url = `${window.location.origin}/deals/${listing.id}`;
    if (navigator.share) {
      await navigator.share({ title: listing.title, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
    api.post(`/p2p/listing/${listing.id}/share`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/20 via-background to-accent/10 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary text-sm font-medium mb-4">
            <ShoppingBag className="w-4 h-4" />
            P2P Marketplace
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            ANANT <span className="text-primary">DEALS</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Buy & Sell anything! List your products, find what you need, or bid in live auctions.
          </p>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/deals/buy-pass"
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all"
            >
              <Tag className="w-5 h-5" />
              Buy Listing Pass
            </Link>
            <Link
              to="/deals/create"
              className="flex items-center gap-2 bg-white/10 border border-white/20 px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create Listing
            </Link>
            <Link
              to="/auctions"
              className="flex items-center gap-2 bg-accent/20 border border-accent/30 px-6 py-3 rounded-xl font-semibold hover:bg-accent/30 transition-all"
            >
              <Gavel className="w-5 h-5" />
              Auction House
            </Link>
            <Link
              to="/display/p2p"
              className="flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 px-6 py-3 rounded-xl font-semibold hover:bg-blue-500/30 transition-all"
              data-testid="display-tv-btn"
            >
              <Tv className="w-5 h-5" />
              Display TV
            </Link>
          </div>
        </div>
      </div>

      {/* Categories Bar */}
      <div className="border-b border-white/10 bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setFilters({ ...filters, category_id: '', subcategory_id: '' })}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                !filters.category_id ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              All Categories
            </button>
            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.icon);
              const hasSubCats = getSubCategoriesForCategory(cat.id).length > 0;
              return (
                <div key={cat.id} className="relative group">
                  <button
                    onClick={() => setFilters({ ...filters, category_id: cat.id, subcategory_id: '' })}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                      filters.category_id === cat.id ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                    {hasSubCats && <ChevronDown className="w-3 h-3" />}
                  </button>
                  {/* Sub-categories dropdown */}
                  {hasSubCats && (
                    <div className="absolute top-full left-0 mt-1 bg-card/95 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[180px] py-1">
                      <div className="text-xs text-muted-foreground px-4 py-1 border-b border-white/10">Sub-categories</div>
                      {getSubCategoriesForCategory(cat.id).map(sub => (
                        <button
                          key={sub.id}
                          onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, category_id: cat.id, subcategory_id: sub.id }); }}
                          className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 transition-colors ${
                            filters.subcategory_id === sub.id ? 'bg-primary/20 text-primary' : ''
                          }`}
                        >
                          {sub.icon && <span className="mr-2">{sub.icon}</span>}
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Show selected sub-category */}
          {filters.subcategory_id && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sub-category:</span>
              <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium">
                {subCategories.find(s => s.id === filters.subcategory_id)?.name}
                <button
                  onClick={() => setFilters({ ...filters, subcategory_id: '' })}
                  className="ml-2 hover:text-white"
                >
                  ×
                </button>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filters & Search */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-4">
          {/* Search */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search listings..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary transition-colors"
            />
          </div>

          {/* Filter Toggle & Type Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                showFilters || filters.city || filters.state 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {(filters.city || filters.state) && (
                <span className="w-2 h-2 bg-white rounded-full"></span>
              )}
            </button>
            
            <div className="flex bg-white/5 rounded-xl p-1">
              <button
                onClick={() => setFilters({ ...filters, listing_type: '' })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  !filters.listing_type ? 'bg-primary text-primary-foreground' : 'hover:bg-white/10'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilters({ ...filters, listing_type: 'sell' })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filters.listing_type === 'sell' ? 'bg-green-500 text-white' : 'hover:bg-white/10'
                }`}
              >
                For Sale
              </button>
              <button
                onClick={() => setFilters({ ...filters, listing_type: 'buy' })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filters.listing_type === 'buy' ? 'bg-blue-500 text-white' : 'hover:bg-white/10'
                }`}
              >
                Wanted
              </button>
            </div>

            <select
              value={filters.sort_by}
              onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
              className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm"
            >
              <option value="created_at">Newest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="views">Most Viewed</option>
            </select>
          </div>
        </div>

        {/* Geo Filters Panel */}
        {showFilters && (
          <div className="glass-card p-4 mb-4 animate-in slide-in-from-top-2">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Location Filter</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  <option value="">All States</option>
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">City</label>
                <input
                  type="text"
                  placeholder="Enter city name..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ ...filters, city: '', state: '' })}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all"
                >
                  Clear Location
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions Bar */}
      {user && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/deals/create"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all"
            >
              <Plus className="w-4 h-4" />
              Create Listing
            </Link>
            <Link
              to="/deals/create/real-estate"
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
            >
              <Building className="w-4 h-4" />
              List Real Estate
            </Link>
            <Link
              to="/deals/buy-pass"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-all"
            >
              <Tag className="w-4 h-4" />
              Buy Listing Pass
            </Link>
          </div>
        </div>
      )}

      {/* Listings Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No listings yet</h3>
            <p className="text-muted-foreground mb-6">Be the first to post a listing!</p>
            <Link
              to="/deals/create"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold"
            >
              <Plus className="w-5 h-5" />
              Create Listing
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <Link
                key={listing.id}
                to={`/deals/${listing.id}`}
                className="group glass-card overflow-hidden hover:border-primary/50 transition-all"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] bg-white/5">
                  {listing.images?.[0] ? (
                    <img
                      src={listing.images[0]}
                      alt={listing.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Type Badge */}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${
                    listing.listing_type === 'sell' 
                      ? 'bg-green-500/90 text-white' 
                      : 'bg-blue-500/90 text-white'
                  }`}>
                    {listing.listing_type === 'sell' ? 'For Sale' : 'Wanted'}
                  </div>
                  
                  {/* Priority Badge */}
                  {listing.is_priority && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/90 text-black">
                      Featured
                    </div>
                  )}
                  
                  {/* Timer */}
                  {listing.expires_at && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-white text-xs">
                      <Clock className="w-3 h-3" />
                      Expires soon
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-1 line-clamp-1 group-hover:text-primary transition-colors">
                    {listing.title}
                  </h3>
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                    {listing.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-primary">
                      ₹{listing.price?.toLocaleString()}
                      {listing.price_negotiable && (
                        <span className="text-xs text-muted-foreground font-normal ml-2">Negotiable</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1 text-muted-foreground text-sm">
                      <MapPin className="w-4 h-4" />
                      {listing.location_city}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {listing.views}
                      </span>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleShare(listing);
                      }}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default P2PDeals;
